// TODO booking api
import { isNil } from 'lodash';
import moment from 'moment';
import {
  AppointmentData,
  BookingData,
  BookingDataState,
  ClientData,
  Consents,
  HealthSummaryQuestionMapping,
  HttpResponse,
  Map, QuestionAndAnswer,
  QuestionAndAnswers,
  RequestStatus,
  RequiredAddress,
} from '../model';
import { CoverType, IDVA, MedicareCard } from '../model/MedicareCard';
import { UPLOAD_STATUS } from '../model/Attachment';
import request from '../infra/request';
import { toZonedIso } from '../utils/timezone';

// Medicare details are only needed with a Medicare card in hand, DVA details only with a DVA card.
export const isCoverComplete = ({
  coverType, medicareCardNumber, medicareCardIRN, expiry, dva,
}: MedicareCard): boolean => {
  if (coverType === CoverType.MEDICARE) {
    return !isNil(medicareCardNumber) && !isNil(medicareCardIRN) && !isNil(expiry);
  }
  if (coverType === CoverType.DVA) {
    return !isNil(dva?.cardType) && !isNil(dva?.cardNumber);
  }
  return coverType === CoverType.MEDICARE_NOT_WITH_ME || coverType === CoverType.NONE;
};

// Medicare and DVA both claim the rebate; only patients with no card do not.
export const needsMedicareClaimingTerm = (coverType?: CoverType) => (
  coverType === CoverType.MEDICARE || coverType === CoverType.MEDICARE_NOT_WITH_ME || coverType === CoverType.DVA
);

export const hasAgreedToTerms = (consents: Consents, coverType?: CoverType) => {
  const required: (keyof Consents)[] = ['isAgreedTheFees', 'isAgreedTheCancelPolicy', 'isAgreedTheContact', 'isAgreedThePrivacyPolicy'];
  if (needsMedicareClaimingTerm(coverType)) required.push('isAgreedTheMedicareClaiming');
  return required.every((key) => consents[key]?.agreed === true);
};

const validateBookingData = (bookingData: BookingDataState): boolean => {
  const {
    appointment, client, medicareCard, questionAndAnswers, consents = {},
  } = bookingData;
  if (isNil(appointment) || isNil(client) || isNil(medicareCard) || isNil(questionAndAnswers)) {
    return false;
  }

  const {
    specialistId, type, date, startTime, aptLength,
  } = appointment as AppointmentData;
  if (isNil(specialistId) || isNil(type) || isNil(date) || isNil(startTime) || isNil(aptLength)) {
    return false;
  }

  const {
    firstName, lastName, dateOfBirth, mobile, email, addressResidential,
  } = client as ClientData;

  const {
    streetAddress, suburb, state, postcode,
  } = addressResidential as RequiredAddress;

  if (isNil(firstName) || isNil(lastName) || isNil(dateOfBirth)
    || isNil(mobile) || isNil(email) || isNil(streetAddress)
    || isNil(suburb) || isNil(state) || isNil(postcode)
  ) {
    return false;
  }

  if (!isCoverComplete(medicareCard as MedicareCard)) {
    return false;
  }

  const { consultWay, gpName } = questionAndAnswers as QuestionAndAnswers;
  if (isNil(consultWay) || isNil(gpName)) {
    return false;
  }

  return hasAgreedToTerms(consents as Consents, (medicareCard as MedicareCard).coverType);
};

export const generateNotes = (originalNotes = '', questions: QuestionAndAnswer[], dva?: IDVA) => {
  const notesWithQuestions = questions.reduce((notes, { question, answer }) => {
    if (answer) {
      return `${notes ? `${notes}\n` : ''}${question}:${answer}`;
    }

    return notes;
  }, originalNotes);

  if (!dva?.hasDept) {
    return notesWithQuestions;
  }

  const dvaSummary = `DVA No.: ${dva.cardNumber}\nDVA Card Type: ${dva.cardType}`;
  return notesWithQuestions ? `${notesWithQuestions}\n${dvaSummary}` : dvaSummary;
};

export const transformBookingData = (bookingData: BookingData) => {
  const data: any = {};
  const {
    appointment, client, medicareCard, questionAndAnswers, consents, attachments, timezone,
  } = bookingData;
  const {
    type, date, startTime, aptLength, specialistId,
  } = appointment as Required<AppointmentData>;
  // On the patient's chosen clock, not the browser's.
  const startDateTime = toZonedIso(date, startTime, timezone);

  data.appointment = {
    appointmentType: type,
    startDateTime,
    endDateTime: moment.parseZone(startDateTime).add(aptLength, 'minutes').format(),
  };
  data.specialist = { specialistId };

  data.healthSummary = {
    questionAndAnswers: Object.keys(questionAndAnswers).map((key) => ({
      question: (HealthSummaryQuestionMapping as Map)[key],
      // @ts-ignore
      answer: questionAndAnswers[key],
    })).filter(({ answer }) => !isNil(answer) && answer !== ''),
  };

  data.client = {
    ...client,
    medicareCard,
    notes1: generateNotes(client.notes1, data.healthSummary.questionAndAnswers, medicareCard.dva),
  };

  if (attachments) {
    // Only finished uploads: a removed, failed or still-uploading file has no file id to send.
    data.healthSummary.attachments = Object.values(attachments)
      .filter(({ fileId, status }) => fileId && (status === UPLOAD_STATUS.DONE || status === UPLOAD_STATUS.SUCCESS))
      .map(({ fileId, fileName }) => ({ fileId, fileName }));
  }

  data.consents = Object.values(consents || {});

  data.metadata = {
    bookingFlowType: 'specialist',
  };

  return data;
};

export const bookingAppointment = async (bookingData: BookingDataState): Promise<HttpResponse<any>> => {
  const isValid = validateBookingData(bookingData);
  if (isValid) {
    const formattedBookingData = transformBookingData(bookingData as BookingData);
    try {
      const { status } = await request.post<any>('/bookings', formattedBookingData, {
        headers: {
          'X-API-KEY': process.env.REACT_APP_API_KEY || '',
        },
      });

      if (status === 201) {
        return { status: RequestStatus.SUCCESS };
      }

      return { status: RequestStatus.FAILURE, error: 'Booking failed' };
    } catch (error) {
      return { status: RequestStatus.FAILURE, error: error as string };
    }
  }

  return { status: RequestStatus.FAILURE, error: 'Request validation failed' };
};
